/**
 * Employee file notes API — admin-only personnel file entries.
 * Never exposed to employees; queries are disabled unless caller is Admin.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, qs } from '../../lib/api.js';
import type { EmployeeFileNote, FileNoteAttachment, Paginated } from '@shared/types';
import {
  fileNoteCreateInput,
  fileNoteUpdateInput,
} from '@shared/schemas/index.js';
import { z } from 'zod';

export type FileNoteCreateBody = z.input<typeof fileNoteCreateInput>;
export type FileNoteUpdateBody = z.input<typeof fileNoteUpdateInput>;

export const fileNoteKeys = {
  all: ['file-notes'] as const,
  list: (params?: { employeeId?: string }) =>
    [...fileNoteKeys.all, 'list', params ?? {}] as const,
};

/** List personnel file notes for an employee (Admin only). */
export function useFileNotes(params?: { employeeId?: string }, opts?: { enabled?: boolean }) {
  return useQuery({
    queryKey: fileNoteKeys.list(params),
    queryFn: () =>
      api.get<Paginated<EmployeeFileNote>>(`/api/file-notes${qs(params)}`),
    enabled: opts?.enabled ?? true,
  });
}

/** Create a personnel file note (Admin only). */
export function useCreateFileNote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: FileNoteCreateBody) =>
      api.post<EmployeeFileNote>('/api/file-notes', body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: fileNoteKeys.all });
    },
  });
}

/** Update a personnel file note (Admin only). */
export function useUpdateFileNote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: FileNoteUpdateBody }) =>
      api.put<EmployeeFileNote>(`/api/file-notes/${id}`, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: fileNoteKeys.all });
    },
  });
}

/** Delete a personnel file note (Admin only). */
export function useDeleteFileNote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete<{ ok: boolean }>(`/api/file-notes/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: fileNoteKeys.all });
    },
  });
}

/** Relative URL that streams an attachment's bytes (auth cookie sent same-origin). */
export function fileNoteAttachmentUrl(noteId: string, attachmentId: string): string {
  return `/api/file-notes/${noteId}/attachments/${attachmentId}/content`;
}

/** Upload a photo or document to a file note (Admin only). */
export function useUploadFileNoteAttachment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ noteId, file }: { noteId: string; file: File }) => {
      const formData = new FormData();
      formData.append('file', file);
      return api.upload<FileNoteAttachment>(`/api/file-notes/${noteId}/attachments`, formData);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: fileNoteKeys.all });
    },
  });
}

/** Remove an attachment from a file note (Admin only). */
export function useDeleteFileNoteAttachment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ noteId, attachmentId }: { noteId: string; attachmentId: string }) =>
      api.delete<{ ok: boolean }>(`/api/file-notes/${noteId}/attachments/${attachmentId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: fileNoteKeys.all });
    },
  });
}
